import React, { useEffect } from 'react';
import {
  Button,
  DatePicker,
  FastFormField,
  intl,
  TextInput,
  Toggle,
  useManagedForm
} from 'ehs-react';
import { usePortletStore } from '../../App';
import type { PersonalForm } from '../../stores/viewState.store';
import { useViewStateStore } from '../../stores/viewState.store';
import * as yup from 'yup';
import { InstitutionHeader } from '../Header/InstitutionHeader';

export function Registration(): JSX.Element {
  const [
    disableAutoSendingNotes,
    urlParameters,
    mandatoryFieldForRegistration,
    isGuest
  ] = usePortletStore(state => [
    state.configuration.disableAutoSendingNotes,
    state.configuration.urlParameters,
    state.configuration.registrationConfig.mandatoryFieldForRegistration,
    state.configuration.guest
  ]);

  const idDisabled =
    !urlParameters.allowEditPatientId && urlParameters.patientId;
  const firstNameDisabled =
    !urlParameters.allowEditPatientFirstName && urlParameters.patientFirstName;
  const lastNameDisabled =
    !urlParameters.allowEditPatientLastName && urlParameters.patientLastName;
  const emailDisabled =
    !urlParameters.allowEditPatientEmail && urlParameters.patientEmail;
  const birthdateDisabled =
    !urlParameters.allowEditPatientBirthdate && urlParameters.patientBirthdate;
  const emailConfirmationDisabled =
    !urlParameters.allowEditEmailConfirmation &&
    urlParameters.sendEmailConfirmation;
  const emailConfirmationHidden = urlParameters.hideEmailConfirmation;

  const isIdentificationMandatory = isGuest
    ? false
    : mandatoryFieldForRegistration === 'IDENTIFICATION';
  const isLastNameMandatory = isGuest
    ? true
    : mandatoryFieldForRegistration === 'LAST_NAME';
  const isFirstNameMandatory = isGuest;

  const [
    previousState,
    nextState,
    updatePatientData,
    updateGuestData,
    setPersonalForm,
    institution,
    getInitialPersonalFormValues
  ] = useViewStateStore(state => [
    state.previousState,
    state.nextState,
    state.updatePatientData,
    state.updateGuestData,
    state.setPersonalForm,
    state.viewStateData?.selectedInstitution,
    state.getInitialPersonalFormValues
  ]);

  let emailConsentLabel;
  if (disableAutoSendingNotes) {
    emailConsentLabel = intl(
      'virtualvisitpatientportlet.patientform.email.consent.without.notes'
    );
  } else {
    emailConsentLabel = intl(
      'virtualvisitpatientportlet.patientform.email.consent'
    );
  }

  const onSubmit = (values: PersonalForm, submissionFinished: () => void) => {
    console.log('onSubmit!!!!!!!', values);
    submissionFinished();
  };
  const lastNameValidator = yup
    .string()
    .matches(
      /^[^<>\"&:;=!@#$%^*()_+`~\[\]\{\}\\|\/?.,0-9]*$/,
      intl('error.CODED.VVPP0030.msg')
    );
  const managedForm = useManagedForm<PersonalForm>({
    formId: 'vvPersonalForm',
    initialValues: getInitialPersonalFormValues(urlParameters),
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialTouched: {
      id: false,
      givenName: false,
      lastName: false,
      birthday: false,
      email: false,
      emailConsent: false
    },
    validationSchema: yup
      .object()
      .shape({
        id: isIdentificationMandatory
          ? yup.string().required()
          : yup.string().notRequired(),
        givenName: isFirstNameMandatory
          ? yup.string().required()
          : yup.string().notRequired(),
        lastName: isLastNameMandatory
          ? lastNameValidator.required()
          : lastNameValidator.notRequired(),
        birthday: yup
          .date()
          .max(
            new Date(),
            intl(
              'virtualvisitpatientportlet.patientform.birthdate.future.warning'
            )
          )
          .defined(),
        email: yup
          .string()
          .email()
          .when('emailConsent', {
            is: true,
            then: (schema: any) => schema.required(),
            otherwise: (schema: any) => schema.notRequired()
          }),
        emailConsent: yup.boolean().defined()
      })
      .required(),
    onSubmit: onSubmit
  });

  useEffect(() => {
    if (isGuest) {
      updateGuestData(managedForm.values);
    } else {
      updatePatientData(managedForm.values);
    }
  }, [updatePatientData, managedForm.values, isGuest, updateGuestData]);

  const onNextClick = () => {
    console.log('nextState: form valid - ' + managedForm.isValid);
    if (managedForm.isValid) {
      nextState();
    }
  };

  const onPrevClick = () => {
    setPersonalForm(managedForm.values);
    previousState();
  };

  return (
    <>
      <div className="vvpp-content">
        <InstitutionHeader institution={institution} />
        <h3 className="vvpp-registration-info">
          {intl('virtualvisitpatientportlet.registration.header')}
        </h3>
        <form {...managedForm.getFormProps()}>
          {!isGuest && (
            <FastFormField
              field={managedForm.getField('id')}
              render={field => (
                <TextInput
                  type="text"
                  label={intl(
                    'virtualvisitpatientportlet.identification.number'
                  )}
                  required={isGuest ? false : isIdentificationMandatory}
                  disabled={idDisabled}
                  {...field}
                />
              )}
            />
          )}
          <FastFormField
            field={managedForm.getField('givenName')}
            render={field => (
              <TextInput
                type="text"
                label={intl('common.name.given')}
                required={isGuest}
                disabled={firstNameDisabled}
                {...field}
              />
            )}
          />
          <FastFormField
            field={managedForm.getField('lastName')}
            render={field => (
              <TextInput
                type="text"
                label={intl('common.name.family')}
                required={isGuest ? true : isLastNameMandatory}
                disabled={lastNameDisabled}
                {...field}
              />
            )}
          />
          {!isGuest && (
            <FastFormField
              field={managedForm.getField('birthday')}
              render={field => (
                <DatePicker
                  variant="single"
                  label={intl('common.birthdate')}
                  allowInput={true}
                  disabled={birthdateDisabled}
                  {...field}
                />
              )}
            />
          )}
          <FastFormField
            field={managedForm.getField('email')}
            render={field => (
              <TextInput
                type="text"
                label={intl('email')}
                required={managedForm.values.emailConsent}
                disabled={emailDisabled}
                {...field}
              />
            )}
          />
          {!isGuest && !emailConfirmationHidden && (
            <Toggle
              label={emailConsentLabel}
              disabled={emailConfirmationDisabled}
              {...managedForm.getField('emailConsent')}
            />
          )}
        </form>
      </div>
      <div className="vvpp-multi-buttons vvpp-buttons-bottom">
        {!isGuest && (
          <Button
            id="vvpp-registration-back-button"
            variant="gray"
            disabled={false}
            onClick={onPrevClick}
            text={intl('view.button.back')}
          />
        )}
        <Button
          id="vvpp-registration-next-button"
          variant="primary"
          disabled={!managedForm.isValid}
          text={
            isGuest
              ? intl('virtualvisitpatientportlet.waitingroom.joinasguest')
              : intl('view.button.next')
          }
          onClick={onNextClick}
        />
      </div>
    </>
  );
}
